import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import getConfig from "next/config";
import { Form, Field } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import classNames from "classnames";

import { addAlert } from "@store/app/actions";
import {
  hasMaxLength,
  isRequired,
  composeFormValidators,
} from "@lib/validations";
import Loading from "@components/Loading";
import InputText from "@components/Form/InputText";
import InputCheckbox from "@components/Form/Checkbox";
import Select from "@components/Form/Select";
import Textarea from "@components/Form/Textarea";
import Heading from "@components/Heading";
import Button from "@components/Button";
import UserFields from "@containers/Contacts/UserFields";
import Success from "@containers/Contacts/Success";

const {
  publicRuntimeConfig: {
    settings: { websiteId, websiteName, baseUrl },
    content: { phone: contactPhone, phoneSecondary: contactPhoneSecondary },
  },
} = getConfig();

class ContactForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isSent: false,
      lastVisibleField: 1,
      attachment: undefined,
      showAdditionalDetailsField: false,
    };

    this.attachmentInput = React.createRef();
    this.formReset = undefined;
  }

  handleFileUpload = (event) => {
    // event.persist() should be used if we try to access event properties
    // in a asynchronous way.
    // By removing the following line, Safari doesn't fire the onChange event
    event.persist();

    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];

      // Make new FileReader
      let reader = new FileReader();

      // Convert the file to base64 text
      reader.readAsDataURL(file);

      reader.onload = () => {
        const base64FileContent = reader.result.split(",")[1];

        let fileInfo = {
          name: file.name,
          type: file.type,
          size: file.size / 1000000, // in megabytes
          base64: base64FileContent,
        };

        // Allow only certain MIME types
        if (
          fileInfo.type !== "image/jpg" &&
          fileInfo.type !== "image/jpeg" &&
          fileInfo.type !== "image/png"
        ) {
          // Alert user
          this.props.addAlert({
            message: "Sunt permise doar fisiere de tipul JPEG, JPG si PNG",
            variant: "warning",
          });

          // Reset input
          this.attachmentInput.current.value = "";
        }

        const fileSizeMax = 5;

        // Allow only certain file sizes
        if (fileInfo.size > fileSizeMax) {
          // Alert user
          this.props.addAlert({
            message: `Sunt permise doar fisiere mai mici de ${fileSizeMax}MB`,
            variant: "warning",
          });

          // Reset input
          this.attachmentInput.current.value = "";
        }

        this.setState({
          attachment: fileInfo,
        });
      };
    } else {
      this.setState({
        attachment: null,
      });
    }
  };

  handleFormSubmit = async (values) => {
    const { message, ...rest } = values;
    const { customer } = this.props;

    const subject = values.Message.split(" ").slice(0, 5).join(" ");
    let attachments = undefined;

    if (this.state.attachment?.name) {
      attachments = {
        Name: this.state.attachment.name,
        Type: this.state.attachment.type,
        Encoding: "base64",
        file: this.state.attachment.base64,
      };
    }

    const formData = {
      Subject: subject,
      Message: message,
      Attachments: attachments,
      StoreId: websiteId,
      StoreName: websiteName,
      ...(this.props.queryParams?.feedback === "1" && {
        origin: "store_feedback",
      }),
      ...(this.props.queryParams?.store_name && {
        PhysicalStoreName: this.props.queryParams.store_name,
      }),
      ...rest,
    };

    this.setState({
      isSending: true,
    });

    const response = await this.props.onFormSubmit(formData);

    if (response) {
      this.setState({
        isSent: true,
        isSending: false,
        selectedLevels: {},
      });

      setTimeout(() => {
        this.formReset({
          fullName: customer && `${customer.first_name} ${customer.last_name}`,
          phone: customer && customer.telephone,
          email: customer && customer.email,
        });
      }, 50);
    } else {
      this.setState({
        isSent: false,
        isSending: false,
      });
    }
  };

  getOptionsForLevelByParentId = ({ options, parentId }) =>
    options.filter((option) => option.ParentSubjectId === parentId);

  removeFormField = ([name], state, { changeValue }) => {
    changeValue(state, name, () => undefined);
  };

  handleFieldChange = ({ form, level, value }) => {
    const formValues = form.getState().values;
    const { fieldsFilteredByLevel, maxLevel } = this.props.formSubjects;
    const maxLevelName = fieldsFilteredByLevel[maxLevel]?.fieldName;

    let lastFieldWithValue = 1;

    // remove upper fields from form state if lower level is changed
    Object.values(fieldsFilteredByLevel).forEach((field) => {
      if (field.level > level && formValues[field.fieldName]) {
        form.mutators.removeFormField(field.fieldName);
      }

      if (formValues[field.fieldName]) {
        lastFieldWithValue = field.level;
      }
    });

    // Rubbish but it works for now...
    if (!value && level < this.state.lastVisibleField) {
      this.setState({
        lastVisibleField: level == 1 ? level : lastFieldWithValue + 1,
      });
    } else if (value && level < maxLevel) {
      this.setState({ lastVisibleField: level + 1 });
    }

    if (formValues[maxLevelName]) {
      this.setState({ showAdditionalDetailsField: true });
    } else if (this.state.showAdditionalDetailsField) {
      //only change state if value is different
      this.setState({ showAdditionalDetailsField: false });
    }
  };

  render() {
    const {
      customer,
      formSubjects: { fieldsFilteredByLevel },
    } = this.props;

    const { isSending, isSent, lastVisibleField, showAdditionalDetailsField } =
      this.state;

    if (!isSent) {
      return (
        <>
          <Form
            onSubmit={this.handleFormSubmit}
            mutators={{ removeFormField: this.removeFormField }}
            initialValues={{
              Name: customer && `${customer.first_name} ${customer.last_name}`,
              Phone: customer && customer.telephone,
              Email: customer && customer.email,
            }}
            render={({ handleSubmit, form, submitting }) => {
              this.formReset = form.reset;

              return (
                <form
                  onSubmit={handleSubmit}
                  autoComplete="false"
                  noValidate={true}
                >
                  {/* User fields */}
                  <UserFields />

                  <p className="mb-5">
                    Raspunsul va fi comunicat in scris pe adresa de e-mail
                    mentionata in cererea transmisa, in termen de maximum 15
                    zile de la data solicitarii tale.
                  </p>

                  <Heading
                    as="h2"
                    display="h1"
                    title="Ai nevoie de suport legat de:"
                    classesProp="mb-3"
                  />

                  <div className="mb-4">
                    <Field
                      component={InputText}
                      id="order_number"
                      name="OrderNo"
                      label="Numarul comenzii"
                      type="text"
                      validate={composeFormValidators(hasMaxLength(100))}
                    />
                  </div>

                  {Object.entries(fieldsFilteredByLevel).map(
                    ([key, { fieldName, label, options }]) => {
                      const level = Number(key);

                      const prevFieldName =
                        fieldsFilteredByLevel[level - 1]?.fieldName;

                      const fieldOptions =
                        level === 1
                          ? options
                          : this.getOptionsForLevelByParentId({
                              options,
                              parentId: form.getState().values[prevFieldName],
                            });

                      return (
                        <div
                          key={level}
                          className={classNames("mb-4", {
                            hidden: level > lastVisibleField,
                          })}
                        >
                          <Field
                            id={`Option-${level}`}
                            name={fieldName}
                            component={Select}
                            label={label}
                            required
                            validate={isRequired()}
                          >
                            <option value="">
                              Selectati {label.toLowerCase()}
                            </option>

                            {fieldOptions.map((item) => (
                              <option
                                key={item.SubjectId}
                                value={item.SubjectId}
                              >
                                {item.Title}
                              </option>
                            ))}
                          </Field>

                          <OnChange name={fieldName}>
                            {(value) => {
                              this.handleFieldChange({
                                form,
                                level,
                                fieldName,
                                value,
                              });
                            }}
                          </OnChange>
                        </div>
                      );
                    }
                  )}

                  {showAdditionalDetailsField && (
                    <>
                      <div className="mb-4">
                        <Field
                          component={Textarea}
                          id="message"
                          name="Message"
                          required
                          validate={composeFormValidators(
                            isRequired(),
                            hasMaxLength(1999)
                          )}
                          label="Te rugam sa faci o scurta descriere a situatiei pentru care ai nevoie de ajutor:"
                        />
                      </div>

                      <div className="mb-4">
                        <label htmlFor="attachment" className="Form-label">
                          Imagine
                        </label>

                        <input
                          id="attachment"
                          name="attachment"
                          type="file"
                          // iOS Safari 12 supports only image/* types
                          accept="image/jpg,image/png,image/jpeg"
                          ref={this.attachmentInput}
                          onChange={this.handleFileUpload}
                        />
                      </div>

                      <div className="mb-4">
                        <Field
                          component={InputCheckbox}
                          id="terms_and_conditions"
                          name="terms_and_conditions"
                          label={
                            <>
                              Sunt de acord cu
                              {` `}
                              <a
                                href={`${baseUrl}/suport-clienti/informatii-generale-termeni-si-conditii/`}
                                title="Termenii si conditiile"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-red-brand"
                              >
                                Termenii si conditiile
                              </a>
                              {` `}
                              de utilizare a site-ului {websiteName}.ro
                            </>
                          }
                          type="checkbox"
                          required
                          validate={isRequired()}
                        />
                      </div>

                      <p className="mb-4">
                        Informatie - Pentru informatii suplimentare, va rugam sa
                        accesati pagina
                        {` `}
                        <a
                          href={`${baseUrl}/protectia-datelor-caracter-personal/pag/`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Protectia Datelor cu Caracter Personal
                        </a>
                        .
                      </p>

                      <div className="lg:w-1/2">
                        <Button
                          type="submit"
                          disabled={submitting}
                          variant="primary"
                          title="Aplica"
                          display="block"
                        />
                      </div>
                    </>
                  )}

                  {isSending && <Loading height={80}>Se trimite</Loading>}
                </form>
              );
            }}
          />
        </>
      );
    }

    return (
      <Success
        contactPhone={contactPhone}
        contactPhoneSecondary={contactPhoneSecondary}
      />
    );
  }
}

export default connect(
  (state) => ({
    customer: state.user.customer,
    formSubjects: state.contacts.formSubjects,
  }),
  { addAlert }
)(ContactForm);
